import {AgentName} from 'common/src/modules/shared/copilot/copilot.enums';
import {Agent, ConversationStarter} from 'common/src/modules/shared/copilot/copilot.types';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import readExcelWorksheetTool from 'common/src/modules/shared/copilot/tools/readExcelWorksheetTool/readExcelWorksheetTool';
import agent from 'common/src/modules/shared/copilot/utils/creators/agent';
import {CLStore} from 'common/src/redux/store';
import campaignContext from '../../contexts/campaignContext';
import createCampaignTool from '../../tools/createCampaignTool';
import navigateToCampaignVersionTool from '../../tools/navigateToCampaignVersionTool';
import findMappingCandidatesTool from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/excelImport/findMappingCandidatesTool/findMappingCandidatesTool';
import getCampaignSignalsTool from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/excelImport/getCampaignSignalsTool';
import sanitizeValuesTool from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/excelImport/sanitizeValuesTool/sanitizeValuesTool';
import validateMappingsTool from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/excelImport/validateMappingsTool/validateMappingsTool';
import createCampaignFromExcelTool from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/excelImport/createCampaignFromExcelTool';
import showExcelImportSummaryTool from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/excelImport/showExcelImportSummaryTool';
import autoArrangeCardsTool from '../campaignSetupAgent/tools/general/autoArrangeCardsTool/autoArrangeCardsTool';
import {reloadCampaign} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/utils/reloadCampaign';

function excelToCampaignAgent(store: CLStore): Agent {
  const conversationStarters: ConversationStarter[] = [];

  const inScope = [
    `ONLY Route to this agent when the user says "Excel To Campaign" explicitly!`,
    `creating a FULL campaign decision tree from an excel file. All other campaign-related operations should be routed to the ${AgentName.CampaignSetup} agent.`,
  ];

  const tools = [
    getFilesTool(),
    readExcelWorksheetTool(),
    getCampaignSignalsTool(),
    findMappingCandidatesTool(),
    validateMappingsTool(),
    sanitizeValuesTool(),
    createCampaignTool({isAllowingSocialCampaigns: false, store}),
    navigateToCampaignVersionTool(store),
    createCampaignFromExcelTool(() => reloadCampaign(store)),
    showExcelImportSummaryTool(),
    autoArrangeCardsTool(store),
  ];

  const role = 'You help the user create an entire campaign decision tree from an excel file.';

  const instructions = `

### Steps:

1.  Prerequisite:  
    a. In order to import an excel file into a campaign, you need to be on the campaign logic step of a campaign.
    b. If you're not in the campaign page, ask the user to either choose an existing campaign by providing its ID (use only the numerical ID) or create a new campaign.
    c. Once a campaign is selected (either created or chosen by the user) use the navigation tool to navigate into that campaign.


2.  **Verify that excel import is enabled for the current campaign type:**
    2.1. If the current campaign is a social campaign, Tell the user: "Importing an Excel file to a campaign is not supported at this time for social campaigns." and end the session.
    2.2. Otherwise, proceed to step 3.

3.  **Get Excel File:**
    Determine which excel file to use by checking the following:
    3.1. Get all uploaded excel files, and ignore any files that are not related to campaign creation.
    3.2. If one of the files was uploaded just now, this is the file to use.
    3.3. If not, look at all the uploaded excel files from step 3.1 and:
         3.3.1. If there is only one file, use this file.
         3.3.2. If there are multiple files, ask the user to choose which file to use.
         3.3.3. If there are no files, ask the user to upload an excel file, using phrasing like "Can you please upload the excel file that you want to import into the campaign?"

4.  **Determine which worksheet to use:**
    4.1. If the excel file contains only one worksheet, use that worksheet.
    4.2. If the excel file contains multiple worksheets, NEVER decide on your own. ALWAYS ask the user which worksheet to use.

5.  **Read Excel File Contents:**
    5.1. Once you've determined which file and worksheet to use, read the worksheet's contents.

6.  **Fetch Campaign Signals:**
    6.1. Retrieve the list of available campaign signals for mapping. 
    6.2. Note which signals have value examples and which do not.

7.  **Map Excel Headers to Campaign Signals**
    7.1. Find mapping candidates for each excel header.
    7.2. Validate the mapping candidates.
    7.3. Converse with the user to fix any issues.
    7.4. Validate a fixed version.
    7.5. Repeat until the mappings are valid.

8.  **Sanitize the excel worksheet:**
    8.1. Get the sanitized values using the appropriate tool.
    8.2. The tool will return sanitized cells, unresolved cells, and instructions how to resolve the unresolved cells. Follow the instructions in order to resolve the unresolved cells.
    8.3. Do not proceed to the next step until ALL unresolved cells are resolved.

9.  **Create Campaign Tree:**
    9.1. Create the campaign according to the excel file, the sanitized values, and the mapping.

10.  **Summarize**
    If the campaign tree was created successfully: 
    10.1 Auto-arrange the cards in the campaign decision tree.
    10.2 Call the show-excel-import-summary tool with a mapping of each Excel header to its card type.
    10.3 Ask the user if there's anything else you can help with. If the user says no, end the session.

### General Guidelines:

- All steps above, besides the summary step, must be done silently, without notifying the user or asking for confirmation, unless the instructions above say otherwise.
- If the user asks to try again, restart the process from step 1.
- The term "signal" is an internal term. Communicate it to the user as "campaign element".
`;

  const contexts = [campaignContext(store)];

  return agent({
    name: AgentName.ExcelToCampaign,
    role,
    conversationStarters,
    inScope,
    tools,
    store,
    instructions,
    contexts,
  });
}

export default excelToCampaignAgent;
